// src/components/pages/message/timecapsule/Step2Recipients.js
import React, { useState, useEffect, useMemo } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { styled } from "@mui/material/styles";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import IconNextArrow from "../../../../assets/icons/icon-next-arrow.svg";
import { ReactComponent as IconTrashRed } from "../../../../assets/icons/icon-trash-red.svg";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { auth, db } from "../../../../firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { ellipsizeProfileText } from "../../../../utils/profileText";
import { filterResolvableConnections } from "../../../../utils/connectionIdentity";

const ContactLimitReachedError = `Limit reached: You can only add up to ${
  process.env.REACT_APP_TIME_CAPSULE_CONTACTS_LIMIT || 25
} contacts.`;
const ContactLimit = process.env.REACT_APP_TIME_CAPSULE_CONTACTS_LIMIT || 25;
const StyledPhoneInput = styled(PhoneInput)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  "& .PhoneInputInput": {
    flex: 1,
    height: "56px",
    borderRadius: "50px",
    paddingLeft: theme.spacing(1),
    fontSize: "1rem",
    border: `1px solid ${theme.palette.grey[400]}`,
    outline: "none",
  },
  "& .PhoneInputCountrySelect": {
    display: "inline-flex",
    alignItems: "center",
    height: "56px",
    paddingLeft: theme.spacing(1),
    background: "transparent",
    border: 0,
    marginRight: theme.spacing(1),
  },
  "& .PhoneInputCountryIcon": {
    verticalAlign: "middle",
  },
}));

// ---------- Helpers for sorting & filtering ----------
const firstNameOf = (name) => (name || "").trim().split(/\s+/)[0] || "";
const hasName = (c) => Boolean((c.displayName || "").trim());

const phoneForSort = (raw) => {
  const s = String(raw || "");
  const withoutPlusOne = s.startsWith("+1") ? s.slice(2) : s;
  return withoutPlusOne.replace(/\D/g, "");
};

const compareContacts = (a, b) => {
  const aHas = hasName(a);
  const bHas = hasName(b);

  if (aHas && bHas) {
    const fa = firstNameOf(a.displayName).toLocaleLowerCase();
    const fb = firstNameOf(b.displayName).toLocaleLowerCase();
    const nameCmp = fa.localeCompare(fb);
    if (nameCmp !== 0) return nameCmp;

    const pa = phoneForSort(a.phoneNumber);
    const pb = phoneForSort(b.phoneNumber);
    return pa.localeCompare(pb, undefined, { numeric: true });
  }

  if (aHas !== bHas) return aHas ? -1 : 1;

  const pa = phoneForSort(a.phoneNumber);
  const pb = phoneForSort(b.phoneNumber);
  return pa.localeCompare(pb, undefined, { numeric: true });
};

export default function Step2Recipients({ wizardData, setWizardData, onNext, onBack }) {
  const [phoneInput, setPhoneInput] = useState("");
  const [showPhoneField, setShowPhoneField] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Firestore-backed connections (shown on iOS + Android)
  const [fsContacts, setFsContacts] = useState([]);
  const [connectionsModalOpen, setConnectionsModalOpen] = useState(false);

  // Selection is a Set of UIDs (stable across sort/filter)
  const [selectedFsContactUids, setSelectedFsContactUids] = useState(() => new Set());

  // Search text for modal filtering
  const [contactSearch, setContactSearch] = useState("");

  const isMobileChrome =
    /Android/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);
  const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/i.test(navigator.userAgent);

  const showConnectionsButton = isIOS || isAndroid;

  const actionBarBottom = useMemo(
    () => "env(safe-area-inset-bottom)",
    []
  );

  // Fetch Firestore connections on iOS + Android
  useEffect(() => {
    if (!showConnectionsButton) return;

    (async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      try {
        const subSnap = await getDocs(collection(db, "users", uid, "connections"));
        const subIds = subSnap.docs.map((d) => d.id);

        const userSnap = await getDoc(doc(db, "users", uid));
        const arrIds = userSnap.data()?.connections || [];

        const allIds = Array.from(
          new Set([...subIds, ...arrIds].map((id) => String(id || "").trim()).filter(Boolean))
        );
        if (!allIds.length) {
          setFsContacts([]);
          return;
        }

        const profiles = (
          await Promise.all(
            allIds.map(async (id) => {
              const userRef = doc(db, "users", id);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                return {
                  uid: userSnap.id,
                  displayName: userSnap.data()?.displayName,
                  email: userSnap.data()?.email,
                  phoneNumber: userSnap.data()?.phoneNumber,
                  profileImage: userSnap.data()?.profileImage,
                  userName: userSnap.data()?.userName,
                  publicHandle: userSnap.data()?.publicHandle,
                };
              }

              const byUidQuery = query(collection(db, "users"), where("uid", "==", id));
              const byUidSnap = await getDocs(byUidQuery);
              if (byUidSnap.empty) return null;
              const matched = byUidSnap.docs[0];
              return {
                uid: matched.id,
                displayName: matched.data()?.displayName,
                email: matched.data()?.email,
                phoneNumber: matched.data()?.phoneNumber,
                profileImage: matched.data()?.profileImage,
                userName: matched.data()?.userName,
                publicHandle: matched.data()?.publicHandle,
              };
            })
          )
        ).filter(Boolean);

        setFsContacts(filterResolvableConnections(profiles));
      } catch (err) {
        console.error("Error loading connections:", err);
      }
    })();
  }, [showConnectionsButton]);

  // Sorted connections
  const sortedFsContacts = useMemo(() => {
    const copy = [...fsContacts];
    copy.sort(compareContacts);
    return copy;
  }, [fsContacts]);

  // Filter by search (matches display name or phone)
  const filteredFsContacts = useMemo(() => {
    const qRaw = contactSearch;
    const q = qRaw.trim().toLowerCase();
    if (!q) return sortedFsContacts;

    const digits = q.replace(/\D/g, "");
    return sortedFsContacts.filter((c) => {
      const name = (c.displayName || "").toLowerCase();
      const phoneRaw = String(c.phoneNumber || "");
      const phoneDigits = phoneRaw.replace(/\D/g, "");

      const nameMatch = name.includes(q);
      const phoneTextMatch = phoneRaw.toLowerCase().includes(q);
      const phoneDigitsMatch = digits.length > 0 && phoneDigits.includes(digits);

      return nameMatch || phoneTextMatch || phoneDigitsMatch;
    });
  }, [sortedFsContacts, contactSearch]);

  const handleSnackbarClose = (e, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleShowPhoneField = () => setShowPhoneField((p) => !p);

  const handleAddPhoneNumber = () => {
    if (!phoneInput || !isValidPhoneNumber(phoneInput)) {
      setSnackbarMessage("Please enter a valid phone number with a country code.");
      setSnackbarOpen(true);
      return;
    }
    if (wizardData.phoneNumbers?.some((item) => item.phone === phoneInput)) {
      setSnackbarMessage("You’ve already added that number.");
      setSnackbarOpen(true);
      return;
    }
    if (wizardData.phoneNumbers.length > ContactLimit - 1) {
      setSnackbarMessage(ContactLimitReachedError);
      setSnackbarOpen(true);
      return;
    }

    setWizardData((p) => ({
      ...p,
      phoneNumbers: [...(p.phoneNumbers || []), { phone: phoneInput }],
    }));
    setPhoneInput("");
  };

  const handleRemovePhoneNumber = (i) => {
    setWizardData((p) => {
      const arr = [...(p.phoneNumbers || [])];
      arr.splice(i, 1);
      return { ...p, phoneNumbers: arr };
    });
  };

  const handleContactPicker = async () => {
    if ("contacts" in navigator && "select" in navigator.contacts) {
      try {
        const props = ["name", "tel"];
        const opts = { multiple: true };
        const contacts = await navigator.contacts.select(props, opts);

        if (contacts.length) {
          const newOnes = [];

          for (const c of contacts) {
            if (Array.isArray(c.tel)) {
              c.tel.forEach((num) => {
                const pn = parsePhoneNumberFromString(num, "US");
                if (pn?.isValid()) {
                  newOnes.push({ name: c.name?.[0], phone: pn.format("E.164") });
                }
              });
            }
          }

          setWizardData((p) => ({
            ...p,
            phoneNumbers: [
              ...(p.phoneNumbers || []),
              ...newOnes.filter(
                (n) => !p.phoneNumbers?.some((item) => item.phone === n.phone)
              ),
            ],
          }));
        }
      } catch {
        setSnackbarMessage("Phone contact access is not supported on this device/browser.");
        setSnackbarOpen(true);
      }
    } else {
      setSnackbarMessage("Phone contact access is not supported on this device/browser.");
      setSnackbarOpen(true);
    }
  };

  // Connections modal handlers (Firestore-backed)
  const handleOpenConnectionsModal = () => {
    if (!fsContacts.length) {
      setSnackbarMessage("No connections found yet.");
      setSnackbarOpen(true);
      return;
    }

    const existingPhones = new Set((wizardData.phoneNumbers || []).map((c) => c.phone));
    const preSelected = new Set(
      fsContacts.filter((c) => existingPhones.has(c.phoneNumber)).map((c) => c.uid)
    );

    setSelectedFsContactUids(preSelected);
    setConnectionsModalOpen(true);
  };

  const handleToggleFsContact = (uid) => {
    setSelectedFsContactUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const handleAddSelectedFsContacts = () => {
    const current = wizardData.phoneNumbers || [];
    if (current.length > ContactLimit - 1) {
      setSnackbarMessage(ContactLimitReachedError);
      setSnackbarOpen(true);
      return;
    }

    const toAdd = fsContacts
      .filter((c) => selectedFsContactUids.has(c.uid))
      .map((c) => ({ name: c.displayName, phone: c.phoneNumber }))
      .filter((n) => !current.some((item) => item.phone === n.phone));

    setWizardData((p) => ({
      ...p,
      phoneNumbers: [...(p.phoneNumbers || []), ...toAdd],
    }));

    setSelectedFsContactUids(new Set());
    setConnectionsModalOpen(false);
    setContactSearch("");
  };

  const handleCloseConnectionsModal = () => {
    setConnectionsModalOpen(false);
    setSelectedFsContactUids(new Set());
    setContactSearch("");
  };

  const handleNextStep = () => {
    const hasPhone = wizardData.phoneNumbers?.length > 0;
    const hasRecips = wizardData.recipients?.length > 0;
    if (!hasPhone && !hasRecips) {
      setSnackbarMessage("Please add at least one recipient.");
      setSnackbarOpen(true);
      return;
    }
    onNext();
  };

  const primaryButtonSx = (t) => ({
    backgroundColor: t.colors.blue,
    color: t.colors.white,
    border: `1px solid ${t.colors.blue}`,
    padding: "6px 10px",
    fontSize: "1rem",
  });

  const secondaryButtonSx = (t) => ({
    backgroundColor: t.colors.white,
    color: t.colors.blue,
    border: `1px solid ${t.colors.blue}`,
    padding: "6px 10px",
    fontSize: "1rem",
  });

  return (
    <Box sx={{ pt: 2, pb: "calc(env(safe-area-inset-bottom) + 110px)" }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        Step 2: Select Recipients
      </Typography>

      <Typography variant="body1" gutterBottom>
        Decide who can see this message and set a time for it to be revealed.
      </Typography>

      {/* Action buttons */}
      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        {/* Android: Phone Contacts (device) */}
        {isMobileChrome && (
          <Button
            variant="contained"
            onClick={handleContactPicker}
            sx={primaryButtonSx}
          >
            Phone Contacts
          </Button>
        )}

        {/* iOS + Android: Contacts (Firestore) */}
        {showConnectionsButton && (
          <Button
            variant="contained"
            onClick={handleOpenConnectionsModal}
            sx={primaryButtonSx}
          >
            My Contacts
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleShowPhoneField}
          sx={secondaryButtonSx}
        >
          Enter Phone #
        </Button>
      </Box>

      {/* Small clarity text under buttons */}
      <Typography variant="caption" sx={{ display: "block", mt: 1.25, opacity: 0.8 }}>
        {isMobileChrome && showConnectionsButton
          ? "Phone Contacts pulls from your device. My Contacts are people you’ve connected with in Inssa."
          : showConnectionsButton
          ? "My Contacts are people you’ve connected with in Inssa."
          : "Add recipients by phone number."}
      </Typography>

      {/* Manual phone input */}
      {showPhoneField && (
        <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center" }}>
          <StyledPhoneInput
            defaultCountry="US"
            international
            countryCallingCodeEditable={false}
            placeholder="Enter phone number"
            value={phoneInput}
            onChange={setPhoneInput}
            type="tel"
          />
          <IconButton
            onClick={handleAddPhoneNumber}
            disabled={wizardData.phoneNumbers?.some((item) => item.phone === phoneInput)}
            sx={(t) => ({
              width: 60,
              height: 60,
              borderRadius: "999px",
              backgroundColor: t.colors.blue,
              color: t.colors.white,
              "&:hover": { backgroundColor: t.colors.darkBlue },
            })}
          >
            <AddIcon />
          </IconButton>
        </Box>
      )}

      {/* List of added numbers */}
      {wizardData.phoneNumbers?.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2, mt: 2 }}>
          {wizardData.phoneNumbers.map((item, idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={(t) => ({ color: t.colors.blue, fontWeight: 700 })}>
                {item.name ? `${item.name} (${item.phone})` : item.phone}
              </Typography>
              <IconButton
                onClick={() => handleRemovePhoneNumber(idx)}
                sx={{ width: 35, height: 35, p: 0, "& svg": { width: 20, height: 20 } }}
              >
                <IconTrashRed />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mt: 3 }}>
        Drop & Reveal!
      </Typography>

      <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
        (Optional) Decide when this message will be available for others.
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Select reveal date"
          value={wizardData.revealDate || null}
          onChange={(v) => setWizardData((p) => ({ ...p, revealDate: v }))}
          minDate={new Date()}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: {
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "999px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "999px" },
                },
              },
              InputProps: {
                endAdornment: wizardData.revealDate && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setWizardData((p) => ({ ...p, revealDate: null }));
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            },
          }}
        />
      </LocalizationProvider>

      {/* Fixed bottom action bar (above bottom nav) */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: actionBarBottom,
          zIndex: (theme) => theme.zIndex.appBar,
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderTopColor: "divider",
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", width: "100%" }}>
          <Button
            variant="outlined"
            onClick={onBack}
            sx={(theme) => ({
              fontWeight: 700,
              px: 3,
              py: 1.25,
              flex: 1,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            })}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={handleNextStep}
            endIcon={
              <Box component="img" src={IconNextArrow} alt="" sx={{ width: 16, height: 16 }} />
            }
            sx={(theme) => ({
              fontWeight: 700,
              border: 0,
              backgroundColor: theme.colors.blue,
              color: theme.palette.common.white,
              px: 3,
              py: 1.25,
              flex: 1,
            })}
          >
            Next Step
          </Button>
        </Box>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          variant="filled"
          sx={(theme) => ({
            width: "100%",
            backgroundColor: theme.colors.red,
          })}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Contacts modal */}
      <Dialog open={connectionsModalOpen} onClose={handleCloseConnectionsModal} fullWidth>
        <DialogTitle>Select My Contacts</DialogTitle>
        <DialogContent dividers>
          {fsContacts.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                placeholder="Search by name or phone"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": { borderRadius: "999px" },
                }}
              />

              <List>
                {filteredFsContacts.map((c) => (
                  <ListItem key={c.uid} button onClick={() => handleToggleFsContact(c.uid)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedFsContactUids.has(c.uid)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={ellipsizeProfileText(c.displayName || "(No name)")}
                      secondary={c.phoneNumber}
                    />
                  </ListItem>
                ))}

                {filteredFsContacts.length === 0 && (
                  <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                    No connections match your search.
                  </Typography>
                )}
              </List>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseConnectionsModal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSelectedFsContacts}
            disabled={selectedFsContactUids.size === 0}
          >
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
